import { Component, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { WebsiteAuditService } from '../website-audit.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/core/services/notification.service';
import { AddTaskComponent } from '../modals/add-task/add-task.component';
import { Router } from '@angular/router';
import { SidenavService } from 'src/app/sidenav/sidenav.service';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import { SeoService } from 'src/app/seo/seo.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { DarkmodeService } from 'src/app/services/darkmode.service';

@Component({
  selector: 'app-management',
  templateUrl: './management.component.html',
  styleUrls: ['./management.component.css'],
})
export class ManagementComponent {
  constructor(
    public router: Router,
    private websiteAuditService: WebsiteAuditService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private sidenavService: SidenavService,
    private seoService: SeoService,
    private localStorageService: LocalStorageService,
    private darkmodeService: DarkmodeService,
  ) {}

  // The sharer's current theme, stamped onto share links so the public page
  // renders in the same dark/light mode the sharer is using.
  private isDark = false;

  confirmDialogRef: MatDialogRef<ConfirmationComponent>;
  addTaskDialogRef: MatDialogRef<AddTaskComponent>;
  subs: Subscription = new Subscription();

  isLoading: boolean = false;
  firstLoader: boolean = false;
  tasks: any = [];

  // Server-side sort (API-bound) for the Type / Frequency / Active columns. Only
  // one column drives the order at a time; null = the API's default order.
  sortBy: 'type' | 'frequency' | 'active' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // ── Server-side search + infinite scroll ───────────────────────────────────
  // The table streams in from the API in small batches as you scroll (virtual
  // scroll); search/sort/paging all run on the server so they cover the whole
  // dataset, not just what's loaded.
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  // Fixed row height the viewport uses to position rows (px).
  tableItemSize: number = 64;
  // Infinite-scroll paging (small batches, fetched as you scroll).
  private page: number = 1;
  private readonly limit: number = 50;
  hasMore: boolean = true;
  loadingMore: boolean = false;
  @ViewChild(CdkVirtualScrollViewport)
  private viewport?: CdkVirtualScrollViewport;

  selectedTask: any = null;

  areThereTasksInProgress: boolean = false;

  private timeout: any;
  private timeoutSeconds: number = 30;

  ngOnInit(): void {
    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        this.isDark = !!isDark;
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        if (res) {
          this.selectedTask = res;
        }
      }),
    );

    // Debounced server-side search — reload from the first batch on each term.
    this.subs.add(
      this.searchSubject
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe((term: string) => {
          this.searchTerm = (term || '').trim();
          this.loadTasks(true);
        }),
    );

    this.firstLoader = true;
    this.loadTasks(true);

    const addAuditClicked = sessionStorage.getItem('addAudit');
    if (addAuditClicked) {
      this.openAddTaskModal();
    }
    sessionStorage.removeItem('addAudit');
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  getDomainFromUrl(url: string): string | null {
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;

      const { hostname } = new URL(normalized);
      return hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }

  // ── Data loading (server-side, infinite scroll) ────────────────────────────
  // reset=true replaces the list with the first batch (used on init, search,
  // sort, mutations); otherwise the next batch is appended as the user scrolls.
  loadTasks(reset: boolean = false, silent: boolean = false) {
    if (reset) {
      this.page = 1;
      this.hasMore = true;
      if (!silent) {
        this.isLoading = true;
      }
    } else {
      if (this.loadingMore || !this.hasMore) {
        return;
      }
      this.loadingMore = true;
    }

    this.subs.add(
      this.websiteAuditService
        .getAuditTasksList({
          page: this.page,
          limit: this.limit,
          search: this.searchTerm,
          sortBy: this.sortBy || undefined,
          sortDirection: this.sortDirection,
        })
        .subscribe({
          next: (res: any) => {
            this.isLoading = false;
            this.loadingMore = false;
            this.firstLoader = false;

            const batch = (res?.tasks || []).map((t: any) => ({
              ...t,
              domain: this.getDomainFromUrl(t?.url),
            }));
            this.tasks = reset ? batch : [...this.tasks, ...batch];

            const pag = res?.pagination;
            this.hasMore = pag ? !!pag.hasNextPage : batch.length >= this.limit;

            this.getStatusOfProjects();
            this.schedulePollIfNeeded();
          },
          error: (err: any) => {
            this.isLoading = false;
            this.loadingMore = false;
            this.firstLoader = false;
            this.notificationService.error(err?.error?.message);
          },
        }),
    );
  }

  // Fetch the next batch (invoked when the viewport scrolls near the end).
  loadMore() {
    if (this.loadingMore || !this.hasMore) {
      return;
    }
    this.page += 1;
    this.loadTasks(false);
  }

  // Virtual-scroll near-bottom detection → prefetch the next batch.
  onScrolledIndexChange() {
    if (!this.viewport || this.loadingMore || !this.hasMore) {
      return;
    }
    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();
    if (total > 0 && end >= total - 15) {
      this.loadMore();
    }
  }

  // Re-poll while any audit is still running so progress bars advance. Refreshes
  // the rows already loaded in a single page, keeping scroll position.
  private schedulePollIfNeeded() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    const inProgress = (this.tasks || []).some(
      (t: any) => t?.progress < 100 && t?.status !== 'Failed',
    );
    if (inProgress) {
      this.timeout = setTimeout(
        () => this.pollRefresh(),
        this.timeoutSeconds * 1000,
      );
    }
  }

  private pollRefresh() {
    const loadedLimit = Math.max(this.page * this.limit, this.limit);
    this.subs.add(
      this.websiteAuditService
        .getAuditTasksList({
          page: 1,
          limit: loadedLimit,
          search: this.searchTerm,
          sortBy: this.sortBy || undefined,
          sortDirection: this.sortDirection,
        })
        .subscribe({
          next: (res: any) => {
            const rows = (res?.tasks || []).map((t: any) => ({
              ...t,
              domain: this.getDomainFromUrl(t?.url),
            }));
            this.tasks = rows;
            const pag = res?.pagination;
            this.hasMore = pag ? !!pag.hasNextPage : rows.length >= loadedLimit;
            this.getStatusOfProjects();
            this.schedulePollIfNeeded();
          },
          error: () => {
            this.schedulePollIfNeeded();
          },
        }),
    );
  }

  onSearchChange(term: string) {
    this.searchSubject.next(term);
  }

  trackByTask(index: number, task: any): string {
    return task?.taskId || String(index);
  }

  // Reload from the first batch and keep the audit-view selection list in sync.
  private refreshAfterMutation() {
    this.loadTasks(true);
    this.websiteAuditService.fetchAuditTasks();
  }

  // ── Column sorts (server-side; Type / Frequency / Active, mutually exclusive) ─
  toggleTypeSort() {
    this.setSort('type');
  }

  toggleFrequencySort() {
    this.setSort('frequency');
  }

  toggleActiveSort() {
    this.setSort('active');
  }

  // Clicking a column asks the API to sort by it; clicking again flips the
  // direction. The sorted order comes back from the server (not re-sorted here).
  private setSort(field: 'type' | 'frequency' | 'active') {
    if (this.sortBy === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortDirection = 'asc';
    }
    this.loadTasks(true);
  }

  getStatusOfProjects() {
    const domains: any = [];

    this.tasks.forEach((element: any) => {
      domains.push(this.getDomainFromUrl(element?.url));
    });

    this.seoService.getStatusOfProject({ domains: domains }).subscribe({
      next: (res: any) => {
        this.firstLoader = false;

        const progresses = res?.progresses || [];

        progresses.forEach((progress: any) => {
          const task = this.tasks.find((task: any) =>
            task?.url.includes(progress?.domain),
          );

          if (task?.averageStatusBar <= 100) {
            task['status'] = 'Pending';
          }
        });
      },
      error: (err) => {
        this.firstLoader = false;
      },
    });
  }

  openTask(task: any, changeTab: boolean = true) {
    this.isLoading = true;
    this.subs.add(
      this.websiteAuditService.getAuditTask(task?.taskId).subscribe({
        next: (res) => {
          const domainFromUrl = this.getDomainFromUrl(res?.url);

          if (!domainFromUrl || res?.type === 'Crawler') {
            this.isLoading = false;
            this.websiteAuditService.updateSelectedAuditTask(res);
            if (changeTab) {
              const tabIndex = res?.type === 'Crawler' ? 7 : 0;
              this.sidenavService.updateActiveTabIndex(tabIndex);
            }
            return;
          }

          this.subs.add(
            this.seoService
              .getStatusOfProject({ domain: domainFromUrl })
              .subscribe({
                next: (organicProgress: any) => {
                  this.isLoading = false;
                  const crawlStatus =
                    organicProgress?.progresses?.[0]?.crawl_status || '';

                  this.websiteAuditService.updateSelectedAuditTask({
                    ...res,
                    organicProgress: {
                      progress: organicProgress?.averageStatusBar,
                      status: crawlStatus,
                    },
                  });

                  if (changeTab) {
                    this.sidenavService.updateActiveTabIndex(0);
                  }
                },
                error: (err) => {
                  this.isLoading = false;
                  this.websiteAuditService.updateSelectedAuditTask({
                    ...res,
                    organicProgress: {
                      progress: 0,
                      status: 'failed',
                    },
                  });

                  if (changeTab) {
                    this.sidenavService.updateActiveTabIndex(0);
                  }
                },
              }),
          );
        },
        error: (err) => {
          this.isLoading = false;
          this.notificationService.error(err?.error?.message);
        },
      }),
    );
  }

  openAddTaskModal() {
    this.addTaskDialogRef = this.dialog.open(AddTaskComponent, {
      width: '430px',
      data: {},
    });
    this.addTaskDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.notificationService.info('Audit added successfully!');
        // Clear any search so the newly added audit is visible in the list.
        this.searchTerm = '';
        this.refreshAfterMutation();
      }
    });
  }

  rerunAudit(item: any) {
    this.confirmDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: 'Rerun Audit',
        content: 'Are you sure you want to rerun this audit?',
      },
    });

    this.subs.add(
      this.confirmDialogRef.afterClosed().subscribe((result) => {
        if (result == 1) {
          item['auditRerunning'] = true;
          this.subs.add(
            this.websiteAuditService.rerunAudit(item).subscribe({
              next: (res: any) => {
                item['auditRerunning'] = false;
                this.notificationService.info('Started rerunning audit');
                this.refreshAfterMutation();
              },
              error: (err: any) => {
                item['auditRerunning'] = false;
                this.notificationService.error(err?.error?.message);
              },
            }),
          );
        }
      }),
    );
  }

  // Convert a one-time audit into a recurring bi-weekly audit.
  // NOTE: relies on a stubbed service endpoint pending backend confirmation.
  convertToBiweekly(item: any) {
    this.confirmDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: 'Convert to Bi-Weekly',
        content:
          'This will convert this one-time audit into a recurring bi-weekly audit. Continue?',
      },
    });

    this.subs.add(
      this.confirmDialogRef.afterClosed().subscribe((result) => {
        if (result != 1) {
          return;
        }
        item['converting'] = true;
        this.subs.add(
          this.websiteAuditService.convertToBiweekly(item.taskId).subscribe({
            next: () => {
              item['converting'] = false;
              this.notificationService.info('Audit converted to bi-weekly.');
              this.refreshAfterMutation();
            },
            error: (err: any) => {
              item['converting'] = false;
              this.notificationService.error(err?.error?.message);
            },
          }),
        );
      }),
    );
  }

  // Toggle an audit active/inactive — confirm first, then PATCH. Reverts the
  // toggle if cancelled or on error.
  toggleAuditActive(item: any, event: any) {
    const desired = !!event?.checked;
    const previous = item?.isActive;

    const revert = () => {
      item.isActive = previous;
      if (event?.source) event.source.checked = previous;
    };

    // Keep the toggle in the desired position while the dialog is open.
    item.isActive = desired;

    this.confirmDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: desired ? 'Activate Audit' : 'Deactivate Audit',
        content: desired
          ? 'Are you sure you want to activate this audit?'
          : 'Are you sure you want to deactivate this audit?',
        confirm_btn: desired ? 'activate' : 'deactivate',
        confirmButtonDangerStyle: !desired,
      },
    });

    this.subs.add(
      this.confirmDialogRef.afterClosed().subscribe((result: any) => {
        if (result != 1) {
          revert();
          return;
        }

        item.activeUpdating = true;
        this.websiteAuditService
          .setAuditActive(item.taskId, desired)
          .subscribe({
            next: () => {
              item.activeUpdating = false;
              this.notificationService.info(
                desired ? 'Audit activated.' : 'Audit deactivated.',
              );
            },
            error: (err: any) => {
              item.activeUpdating = false;
              revert();
              this.notificationService.error(
                err?.error?.message || 'Could not update audit status.',
              );
            },
          });
      }),
    );
  }

  openDeleteAudit(item: any) {
    this.confirmDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: 'Delete Audit',
        content: 'Are you sure you want to delete this audit?',
      },
    });

    this.subs.add(
      this.confirmDialogRef.afterClosed().subscribe((result) => {
        if (result == 1) {
          this.subs.add(
            this.websiteAuditService.deleteAudit(item.taskId).subscribe({
              next: (res: any) => {
                this.notificationService.info('Audit deleted successfully!');
                this.refreshAfterMutation();
              },
              error: (err: any) => {
                this.notificationService.error(err?.error?.message);
              },
            }),
          );
        }
      }),
    );
  }

  private getHubspotName(): string {
    const email: string = this.localStorageService.get('email') || '';

    if (email.includes('brooke')) {
      return 'brooke-eichhorn';
    } else if (email.includes('valon') || email.includes('flamur')) {
      return 'valon-xhafa';
    } else if (email.includes('dahoud') || email.includes('daoud')) {
      return 'marcel-dahoud';
    } else {
      return 'patrick-fischer1';
    }
  }

  shareAudit(item: any) {
    this.subs.add(
      this.websiteAuditService
        .getAuditTaskShareableToken({
          taskId: item.taskId,
          theme: this.isDark ? 'dark' : 'light',
        })
        .subscribe({
          next: (res: any) => {
            const type = encodeURIComponent(item?.type || '');
            navigator.clipboard.writeText(
              `${window.location.origin}/share/${res.token}?hubspot=${this.getHubspotName()}&type=${type}`,
            );
            this.notificationService.info('Shareable url copied!');
          },
          error: (err: any) => {
            this.notificationService.error(err?.error?.message);
          },
        }),
    );
  }
}
